import {
  Alert,
  Autocomplete,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useEffect, useMemo, useState } from 'react'
import {
  useCreateGatePass,
  useEquipmentItems,
  useHalls,
  useOrders,
  useGatePassTemplates,
  useStaff,
  useUpdateGatePass,
  useVehicles,
} from '@/api/hooks'
import type { EquipmentItem, GatePass, GatePassTemplate } from '@/api/types'
import { ErrorBanner, formatNumber } from '@/components/common'

export type PassType = 'OUTDOOR_VESSEL' | 'HALL_ASSET'

/** Which catalogue category each document draws its items from. */
const CATALOGUE_CATEGORY: Record<PassType, string | undefined> = {
  OUTDOOR_VESSEL: 'VESSEL',
  HALL_ASSET: 'HALL_ASSET',
}

type DraftLine = {
  equipmentItemId: string
  issuedQuantity: string
  conditionOnIssue: string
}

/** The order a pass is being raised against, when the caller already knows it. */
export type PassOrder = {
  id: string
  orderNumber: string
  customerName: string
  outletId?: string
  hallId?: string
}

/**
 * Raises a gate pass: vessels going out to an outdoor job, or hall property
 * handed to a guest for the night.
 *
 * <p>Opened either from the gate pass register, where the order is chosen, or
 * from the order itself, where it is already known and fixed - the same document
 * either way, so the same form raises it.</p>
 */
export function GatePassDialog({
  open,
  type,
  order,
  pass,
  onClose,
}: {
  open: boolean
  type: PassType
  /** Fixed when raised from an order; omitted when raised from the register. */
  order?: PassOrder
  /**
   * The draft being corrected. A pass that has gone out is not edited: what
   * left the store is a fact, and the way to change it is to record what came
   * back.
   */
  pass?: GatePass | null
  onClose: () => void
}) {
  const vessels = type === 'OUTDOOR_VESSEL'
  // Only fetch the order list when the caller has not already fixed one.
  const { data: orders } = useOrders(order ? { size: 1, status: 'CONFIRMED' } : { status: 'CONFIRMED', size: 100 })
  const { data: halls } = useHalls()
  const { data: vehicles } = useVehicles({})
  // Drivers, not everybody. A steward in the list is a steward somebody
  // eventually picks, and the pass then names a person who cannot drive.
  const { data: drivers } = useStaff({ role: 'DRIVER' })
  const { data: items } = useEquipmentItems(CATALOGUE_CATEGORY[type])
  const create = useCreateGatePass()
  const update = useUpdateGatePass()
  const { data: templates } = useGatePassTemplates(type)
  const editing = Boolean(pass)

  const [orderId, setOrderId] = useState(order?.id ?? '')
  const [hallId, setHallId] = useState(order?.hallId ?? '')
  const [vehicleId, setVehicleId] = useState('')
  const [driverStaffId, setDriverStaffId] = useState('')
  const [issuedToName, setIssuedToName] = useState('')
  const [issuedToMobile, setIssuedToMobile] = useState('')
  const [expectedReturnAt, setExpectedReturnAt] = useState('')
  const [lines, setLines] = useState<DraftLine[]>([])
  // The entry line above the list, cleared after each add.
  const [picked, setPicked] = useState<EquipmentItem | null>(null)
  const [quantity, setQuantity] = useState('')
  const [condition, setCondition] = useState('')

  /**
   * Adds what is in the entry line to the pass. The same item twice adds to the
   * quantity already there rather than listing it again - a second line for the
   * same vessel is two counts of one thing coming back.
   */
  // Prefilled from the pass being corrected, each time it is opened on a
  // different one.
  useEffect(() => {
    if (!open || !pass) return
    setOrderId(pass.orderId)
    setHallId(pass.hallId ?? '')
    setVehicleId(pass.vehicleId ?? '')
    setDriverStaffId(pass.driverStaffId ?? '')
    setIssuedToName(pass.issuedToName ?? '')
    setIssuedToMobile(pass.issuedToMobile ?? '')
    setExpectedReturnAt(pass.expectedReturnAt ? pass.expectedReturnAt.slice(0, 16) : '')
    setLines(pass.lines.map((line) => ({
      equipmentItemId: line.equipmentItemId,
      issuedQuantity: String(line.issuedQuantity),
      conditionOnIssue: line.conditionOnIssue ?? '',
    })))
  }, [open, pass?.id])

  /**
   * Fills the pass from a saved list. It replaces what is there rather than
   * adding to it: loading a template onto a half-typed pass and getting both
   * is how a vessel goes out twice.
   */
  const loadTemplate = (templateId: string) => {
    const template = templates?.find((candidate) => candidate.id === templateId)
    if (!template) return
    setLines(template.lines.map((line: GatePassTemplate['lines'][number]) => ({
      equipmentItemId: line.equipmentItemId,
      issuedQuantity: String(line.quantity),
      conditionOnIssue: '',
    })))
  }

  const addLine = () => {
    if (!picked || Number(quantity) <= 0) return
    setLines((current) => {
      const at = current.findIndex((line) => line.equipmentItemId === picked.id)
      if (at === -1) {
        return [...current, {
          equipmentItemId: picked.id,
          issuedQuantity: quantity,
          conditionOnIssue: condition,
        }]
      }
      return current.map((line, index) =>
        index === at
          ? { ...line, issuedQuantity: String(Number(line.issuedQuantity) + Number(quantity)) }
          : line,
      )
    })
    setPicked(null)
    setQuantity('')
    setCondition('')
  }

  // Raised from an order, the order and its hall are given, not chosen.
  useEffect(() => {
    if (!open || !order) return
    setOrderId(order.id)
    setHallId(order.hallId ?? '')
  }, [open, order])

  const selectedOrder = order ?? orders?.content?.find((candidate) => candidate.id === orderId)

  const usable = useMemo(
    () => lines.filter((line) => line.equipmentItemId && Number(line.issuedQuantity) > 0),
    [lines],
  )
  const canSubmit = Boolean(orderId) && usable.length > 0 && (!vessels ? Boolean(hallId) : true)
  const emptyCatalogue = (items ?? []).length === 0

  const submit = () => {
    const body = {
      orderId,
      outletId: selectedOrder?.outletId ?? pass?.outletId,
      hallId: hallId || undefined,
      vehicleId: vessels ? vehicleId || undefined : undefined,
      driverStaffId: vessels ? driverStaffId || undefined : undefined,
      issuedToName: issuedToName || undefined,
      issuedToMobile: issuedToMobile || undefined,
      expectedReturnAt: expectedReturnAt ? new Date(expectedReturnAt).toISOString() : undefined,
      lines: usable.map((line) => ({
        equipmentItemId: line.equipmentItemId,
        issuedQuantity: Number(line.issuedQuantity),
        conditionOnIssue: line.conditionOnIssue || undefined,
      })),
    }

    if (pass) {
      update.mutate({ id: pass.id, body }, { onSuccess: onClose })
      return
    }

    create.mutate(
      { type, body },
      {
        onSuccess: () => {
          setOrderId(order?.id ?? '')
          setHallId(order?.hallId ?? '')
          setVehicleId('')
          setDriverStaffId('')
          setIssuedToName('')
          setIssuedToMobile('')
          setExpectedReturnAt('')
          setLines([])
          setPicked(null)
          setQuantity('')
          setCondition('')
          onClose()
        },
      },
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {editing
          ? `Correct ${pass?.issueNumber}`
          : vessels
            ? 'New vessel outpass'
            : 'New hall asset handover'}
      </DialogTitle>
      <DialogContent>
        <ErrorBanner error={create.error ?? update.error} />

        {/* The picker draws on the store, so an empty one is a store problem
            and the message says where to go and fix it. */}
        {vessels && emptyCatalogue ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Nothing in the store is marked as a vessel, so there is nothing to put on this pass.
            Mark them under <strong>Gate passes → Store items</strong>.
          </Alert>
        ) : null}
        <Alert severity="info" sx={{ mb: 2 }}>
          The pass is saved as a draft. Stock only leaves the store when you dispatch it.
        </Alert>

        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={6}>
            {order ? (
              <TextField
                fullWidth
                label="Order"
                value={`${order.orderNumber} — ${order.customerName}`}
                disabled
                helperText="Raised against this order"
              />
            ) : (
              <TextField
                select
                fullWidth
                label="Order"
                value={orderId}
                onChange={(event) => setOrderId(event.target.value)}
              >
                {(orders?.content ?? []).map((candidate) => (
                  <MenuItem key={candidate.id} value={candidate.id}>
                    {candidate.orderNumber} — {candidate.customerName}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>

          {vessels ? (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Vehicle"
                  value={vehicleId}
                  onChange={(event) => {
                    setVehicleId(event.target.value)
                    // The register already knows who usually takes it out.
                    const chosen = vehicles?.find((v) => v.id === event.target.value)
                    if (chosen?.defaultDriverStaffId) {
                      setDriverStaffId(chosen.defaultDriverStaffId)
                    }
                  }}
                >
                  <MenuItem value="">Not assigned</MenuItem>
                  {(vehicles ?? []).map((vehicle) => (
                    <MenuItem key={vehicle.id} value={vehicle.id}>
                      {vehicle.registrationNumber}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Driver"
                  value={driverStaffId}
                  onChange={(event) => setDriverStaffId(event.target.value)}
                  helperText={
                    (drivers ?? []).length === 0
                      ? 'Nobody on the staff master is a driver yet'
                      : undefined
                  }
                >
                  <MenuItem value="">Not assigned</MenuItem>
                  {(drivers ?? []).map((person) => (
                    <MenuItem key={person.id} value={person.id}>
                      {person.fullName}
                      {person.mobile ? ` · ${person.mobile}` : ''}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Received by at venue"
                  value={issuedToName}
                  onChange={(event) => setIssuedToName(event.target.value)}
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Hall"
                  value={hallId}
                  onChange={(event) => setHallId(event.target.value)}
                  helperText="Required — the assets belong to this hall"
                >
                  {(halls ?? []).map((hall) => (
                    <MenuItem key={hall.id} value={hall.id}>
                      {hall.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Handed to (guest)"
                  value={issuedToName}
                  onChange={(event) => setIssuedToName(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Guest mobile"
                  value={issuedToMobile}
                  onChange={(event) => setIssuedToMobile(event.target.value)}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="datetime-local"
              label="Due back"
              value={expectedReturnAt}
              onChange={(event) => setExpectedReturnAt(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        {/* Entered the way a KOT or a bill is: pick the item, say how many,
            add it. The line joins the list below and the box clears for the
            next one - rather than growing a table of half-filled blank rows. */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
          sx={{ mt: 3, mb: 1 }}
        >
          <Typography variant="subtitle2">Items</Typography>
          {/* Reached for first on a busy morning: the list of what this kind of
              job usually takes, loaded and then trimmed. */}
          {(templates ?? []).length > 0 ? (
            <TextField
              select
              size="small"
              label="Load a template"
              value=""
              onChange={(event) => loadTemplate(event.target.value)}
              sx={{ minWidth: 260 }}
              helperText="Replaces what is listed below"
            >
              {(templates ?? []).map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name} · {template.lines.length} items
                </MenuItem>
              ))}
            </TextField>
          ) : null}
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start" sx={{ mb: 2 }}>
          <Autocomplete
            size="small"
            options={items ?? []}
            value={picked}
            onChange={(_event, chosen) => setPicked(chosen)}
            getOptionLabel={(option) =>
              `${option.code} — ${option.name} (${formatNumber(option.stockQuantity)} in stock)`
            }
            isOptionEqualToValue={(option, chosen) => option.id === chosen.id}
            sx={{ flexGrow: 1, minWidth: 280 }}
            renderInput={(params) => <TextField {...params} label="Item" autoFocus />}
          />
          <TextField
            size="small"
            type="number"
            label="Quantity"
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            onKeyDown={(event) => {
              // Enter adds the line, so a whole load goes out without the mouse.
              if (event.key === 'Enter') {
                event.preventDefault()
                addLine()
              }
            }}
            sx={{ width: 120 }}
            helperText={picked?.unit}
          />
          <TextField
            size="small"
            label="Condition"
            value={condition}
            onChange={(event) => setCondition(event.target.value)}
            placeholder="good"
            sx={{ width: 150 }}
          />
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            disabled={!picked || Number(quantity) <= 0}
            onClick={addLine}
          >
            Add
          </Button>
        </Stack>

        {lines.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nothing on this pass yet. Add the first item above.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell>Condition on issue</TableCell>
                <TableCell sx={{ width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line, index) => {
                const item = items?.find((candidate) => candidate.id === line.equipmentItemId)
                return (
                  <TableRow key={`${line.equipmentItemId}-${index}`} hover>
                    <TableCell>
                      {item?.name ?? line.equipmentItemId}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {item?.code}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(Number(line.issuedQuantity))} {item?.unit ?? ''}
                    </TableCell>
                    <TableCell>{line.conditionOnIssue || '—'}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        aria-label={`Remove ${item?.name ?? 'line'} ${index + 1}`}
                        onClick={() => setLines((current) => current.filter((_, at) => at !== index))}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )
              })}
              <TableRow>
                <TableCell>
                  <strong>{lines.length} line(s)</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>
                    {formatNumber(lines.reduce((sum, line) => sum + Number(line.issuedQuantity || 0), 0))}
                  </strong>
                </TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!canSubmit || create.isPending || update.isPending}
        >
          {editing ? 'Save correction' : 'Save draft'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
